import { useCallback, useEffect, useState, useMemo } from "react";
// Imports from antd are done individually to minimize bundlesize
import Button from "antd/es/button";
import Modal from "antd/es/modal";
import Input from "antd/es/input";
import Select from "antd/es/select";
import Empty from "antd/es/empty";
import Tabs from "antd/es/tabs";
import Col from "antd/es/col";
import Card from "antd/es/card";
import Statistic from "antd/es/statistic";
import message from "antd/es/message";
import "./dashboard.css";
import { useForm, Controller } from "react-hook-form";
import Layout from "./Layout";

import { debounce, sortBy } from "lodash";
import { useParams, useNavigate } from "react-router";
import DOMPurify from "dompurify";
import Calendar from "./Calendar";
import ReservationsTable from "./ReservationsTable";
import CostTable from "./CostTable";
import InvoiceList from "./InvoiceList";
import SettlementRules from "./SettlementRules";
import RentalInfo from "./RentalInfo";
import LandlordInfo from "./LandlordInfo";
import {
  checkRole,
  Period,
  dashboardPath,
  normalizeDashboardTab,
} from "./utils";
import PeriodSelect from "./PeriodSelect";
import OwnerBookingModal from "./OwnerBookingModal";
import { useIntl } from "react-intl";
import {
  CUT_OFF,
  COMPANY_INFO,
  DASHBOARD_TABS,
  DEFAULT_DASHBOARD_TAB,
} from "./constants";
import * as Sentry from "@sentry/react";
import api from "./api";

import dayjs from "dayjs";
import html2pdf from "html2pdf.js";

const { REACT_APP_API_URL: API_URL, REACT_APP_BRAND_NAME } = process.env;

const { Option } = Select;
const { TextArea } = Input;

dayjs().format();

export function logOut(intl, handleUnauthorized = true) {
  if (handleUnauthorized) {
    // used when 401 error is returned from the API
    Sentry.logger.info("Session expired - logging out user");
    message.warning(intl.formatMessage({ id: "session-expired" }));
  } else {
    Sentry.logger.info("User logged out");
  }
  Sentry.setUser(null);
  localStorage.clear();
  window.location.href = "/login";
}

export function genericError(intl, error = null, context = {}) {
  // used when error other than 401 is returned from the API
  const isNetwork =
    error?.code === "ERR_NETWORK" ||
    error?.code === "ECONNABORTED" ||
    error?.message === "Network Error" ||
    (error && !error.response);

  if (error instanceof Error) {
    // Only capture non-network errors as exceptions (network errors are expected/transient)
    if (!isNetwork) {
      Sentry.captureException(error, {
        contexts: { custom: { message: "Generic error occurred", ...context } },
      });
    }
    Sentry.logger.error("Generic error occurred", {
      error: error.message,
      stack: error.stack,
      isNetworkError: isNetwork,
      online: navigator.onLine,
      ...context,
    });
  } else {
    Sentry.logger.error("Generic error occurred", { error, ...context });
  }

  // Show appropriate message based on error type
  if (isNetwork) {
    if (!navigator.onLine) {
      message.error(intl.formatMessage({ id: "offline-error" }));
    } else {
      message.error(intl.formatMessage({ id: "network-error" }));
    }
  } else {
    message.error(intl.formatMessage({ id: "generic-error" }));
  }
}

export default function Dashboard() {
  const {
    rentalId: pathRentalId,
    landlordId: pathLandlordId,
    yearFilter: pathYearFilter,
    monthFilter: pathMonthFilter,
    tab: pathTab,
  } = useParams();

  const navigate = useNavigate();
  const intl = useIntl();
  const isAdmin = checkRole(localStorage);

  const selectedRental = pathRentalId || null;
  const selectedLandlord = pathLandlordId || null;

  const period = useMemo(
    () => Period.fromPath(pathMonthFilter, pathYearFilter, dayjs()),
    [pathMonthFilter, pathYearFilter]
  );

  // The URL only has room for a tab once a rental is selected. Until then
  // (admins landing on a bare /dashboard) this state keeps the tabs clickable.
  const [pendingTab, setPendingTab] = useState(DEFAULT_DASHBOARD_TAB);

  // Derived rather than stored, so the redirect effects below always read the
  // tab the URL currently shows - state would leave them a render behind and
  // they would fight the back button.
  const activeTab = pathRentalId
    ? normalizeDashboardTab(pathTab, isAdmin)
    : pendingTab;

  const [triedLoading, setTriedLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const settlement_period =
    period && period.isQuarterly() ? "QUARTERLY" : "MONTHLY";

  const [rentalOwnerships, setRentalOwnerships] = useState([]);
  const [contract, setContract] = useState(null);
  const [landlords, setLandlords] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [columns, setColumns] = useState([]);
  const [costs, setCosts] = useState([]);
  const [reimbursements, setReimbursements] = useState([]);
  const [settlement, setSettlement] = useState(null);
  const [rejectNoteVisible, setRejectNoteVisible] = useState(false);
  const [companyDataVisible, setCompanyDataVisible] = useState(false);
  const [invoiceAcceptNoteVisible, setInvoiceAcceptNoteVisible] =
    useState(false);
  const [faqContent, setFaqContent] = useState(null);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const [isSettlementLoading, setIsSettlementLoading] = useState(false);

  const [ownerBookingVisible, setOwnerBookingVisible] = useState(false);

  const rentalOwnership =
    selectedRental && rentalOwnerships
      ? rentalOwnerships.find(
          (ro) =>
            ro.rental.bookingsync_rental_id === selectedRental &&
            ro.landlord.id === selectedLandlord
        )
      : null;

  const filteredSearchResults = [
    ...new Set(
      searchResults && searchResults.map((result) => result.rental.name)
    ),
  ];

  const rentals = [
    ...new Set(
      rentalOwnerships && rentalOwnerships.map((result) => result.rental.name)
    ),
  ];

  const rental = rentalOwnership && rentalOwnership.rental;
  const landlord = rentalOwnership && rentalOwnership.landlord;
  const viewOnly = rentalOwnership ? rentalOwnership.view_only : true;
  const rentalShare = rentalOwnership ? rentalOwnership.share : 1;

  // Set rental/period/tab context for all logs in this session. Kept separate
  // from the fetch effect below so switching tabs doesn't refetch.
  useEffect(() => {
    if (rental && period) {
      Sentry.setContext("dashboard", {
        rentalId: selectedRental,
        rentalName: rental.name,
        landlordId: selectedLandlord,
        period: period.toString(),
        tab: activeTab,
      });
    }
  }, [rental, selectedRental, selectedLandlord, period, activeTab]);

  useEffect(() => {
    if (rental && period) {
      getContract(selectedRental, selectedLandlord, period);
      getSettlement(selectedRental, selectedLandlord, period);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rental, selectedRental, selectedLandlord, period]);

  useEffect(() => {
    if (selectedRental) {
      getFaqContent(selectedRental);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRental]);

  const findMatchingRental = (rentalName, period) => {
    let matches;
    let matching_rental;

    const matching_rentals = rentalOwnerships.filter(
      (ro) => ro.rental.name === rentalName
    );

    if (matching_rentals.length === 0) {
      matching_rental = null;
    } else if (matching_rentals.length === 1) {
      matching_rental = matching_rentals[0];
    } else {
      matches = matching_rentals.map((ro) => {
        if (ro.rental.name === rentalName) {
          const start = dayjs(ro.start).hour(12);
          const end = ro.end && dayjs(ro.end).hour(12);

          let score;

          const check =
            (start.year() < period.toDate().year() ||
              (start.year() === period.toDate("end").year() &&
                start.month() <= period.toDate("end").month())) &&
            ((end && end.year() > period.toDate("start").year()) ||
              (end &&
                end.year() === period.toDate("start").year() &&
                end.month() >= period.toDate("start").month()) ||
              !end);

          if (check) {
            score = 0;
          } else {
            const availablePeriod = ro.end
              ? dayjs(ro.start).diff(period.toDate(), "days") >
                period.toDate().diff(dayjs(ro.end), "days")
                ? Period.fromDate(dayjs(ro.start))
                : Period.fromDate(dayjs(ro.end))
              : Period.fromDate(dayjs(ro.start));

            score = Math.abs(
              dayjs(period.toDate()).diff(availablePeriod.toDate(), "days")
            );
          }

          return {
            matching_rental: ro,
            score,
          };
        }

        return null;
      });

      matching_rental = matches.sort((a, b) => a.score - b.score)[0]
        .matching_rental;
    }

    return matching_rental;
  };

  const checkIfPeriodAvaialble = (rental, period, checkMatching = true) => {
    if (!rental) return false;

    if (
      !isAdmin &&
      (Number(period.year) <
        CUT_OFF[REACT_APP_BRAND_NAME][settlement_period]["year"] ||
        (Number(period.year) ===
          CUT_OFF[REACT_APP_BRAND_NAME][settlement_period]["year"] &&
          period.month.id <=
            CUT_OFF[REACT_APP_BRAND_NAME][settlement_period]["month"]))
    ) {
      return false;
    }

    const start = dayjs(rental.start);
    const end = rental.end && dayjs(rental.end);

    const isAvailable =
      (start.year() < period.toDate().year() ||
        (start.year() === period.toDate("end").year() &&
          start.month() <= period.toDate("end").month())) &&
      ((end && end.year() > period.toDate("start").year()) ||
        (end &&
          end.year() === period.toDate("start").year() &&
          end.month() >= period.toDate("start").month()) ||
        !end);

    if (!isAvailable && checkMatching) {
      const matching_rental = findMatchingRental(rental.rental.name, period);
      return checkIfPeriodAvaialble(matching_rental, period, false);
    }

    return isAvailable;
  };

  const performRentalChange = (
    rental,
    landlord,
    period,
    check = true,
    replace = false
  ) => {
    const change = checkIfPeriodAvaialble(rental, period, check);

    if (change) {
      Sentry.logger.trace("Navigating to rental", {
        rentalId: rental.rental.bookingsync_rental_id,
        landlord,
        period: period.toString(),
      });
      navigate(
        dashboardPath(
          rental.rental.bookingsync_rental_id,
          landlord,
          period,
          activeTab
        ),
        { replace }
      );
    } else {
      const availablePeriod = rental.end
        ? dayjs(rental.start).diff(period.toDate(), "days") >
          period.toDate().diff(dayjs(rental.end), "days")
          ? Period.fromDate(dayjs(rental.start))
          : Period.fromDate(dayjs(rental.end))
        : Period.fromDate(dayjs(rental.start));

      Sentry.logger.debug("Period adjusted - requested period unavailable", {
        requestedPeriod: period.toString(),
        adjustedPeriod: availablePeriod.toString(),
        rentalId: rental.rental.bookingsync_rental_id,
      });
      navigate(
        dashboardPath(
          rental.rental.bookingsync_rental_id,
          landlord,
          availablePeriod,
          activeTab
        ),
        { replace }
      );
    }
  };

  const selectRental = (rentalName, landlordId, period) => {
    Sentry.logger.info("Rental selected", {
      rentalName,
      landlordId,
      period: period.toString(),
    });

    const matching_rental = findMatchingRental(rentalName, period);

    if (
      landlordId === null ||
      (matching_rental && matching_rental.start !== rentalOwnership.start)
    ) {
      landlordId =
        matching_rental && matching_rental.landlord
          ? matching_rental.landlord.id
          : pathLandlordId;
    }

    if (matching_rental) {
      Sentry.logger.info("Switching to rental", {
        rentalName,
        rentalId: matching_rental.rental.bookingsync_rental_id,
        landlordId,
        period: period.toString(),
      });
      performRentalChange(matching_rental, landlordId, period, false);
    } else {
      Sentry.logger.warn("Rental not found", {
        rentalName,
        period: period.toString(),
      });
    }
  };

  async function getSettlement(bookingsync_rental_id, landlordId, period) {
    Sentry.logger.debug("Loading settlement data", {
      rentalId: bookingsync_rental_id,
      landlordId,
      period: period.toString(),
    });

    await api(
      {
        method: "get",
        url: `${API_URL}/costs/portal?bookingsync_rental_id=${bookingsync_rental_id}&landlord_id=${landlordId}&period=${period.toString()}`,
        headers: { Authorization: `Bearer ${localStorage.token}` },
      },
      []
    )
      .then((response) => {
        setSettlement(response && response.data && response.data.settlement);
        setBookings(
          sortBy(
            response.data.bookings.data.map((booking) => {
              return {
                ...booking,
                from_date: dayjs(booking.from_date).format("YYYY-MM-DD"),
                until_date: dayjs(booking.until_date).format("YYYY-MM-DD"),
              };
            }),
            "from_date"
          )
        );
        setColumns(response.data.bookings.columns);
        setCosts(response.data.costs);
        setReimbursements(response.data.reimbursements);

        Sentry.logger.info("Settlement data loaded", {
          rentalId: bookingsync_rental_id,
          settlementId: response.data.settlement?.id,
          bookingsCount: response.data.bookings.data.length,
        });
      })
      .catch((error) => {
        const status = error.response?.status;

        if (status === 401) {
          logOut(intl);
        } else {
          Sentry.captureException(error, {
            contexts: {
              custom: {
                message: "Failed to load settlement",
                rentalId: bookingsync_rental_id,
                landlordId,
                period: period.toString(),
              },
            },
          });
          Sentry.logger.error("Failed to load settlement", {
            error: error.message,
            rentalId: bookingsync_rental_id,
            landlordId,
            period: period.toString(),
          });
          genericError(intl, error, { endpoint: "/costs/portal" });
        }
      });
  }

  async function getFaqContent(rental) {
    api(
      {
        method: "get",
        url: `${API_URL}/costs/faq?rental=${rental}`,
        headers: { Authorization: `Bearer ${localStorage.token}` },
      },
      []
    )
      .catch((error) =>
        error.response && error.response.status && error.response.status === 401
          ? logOut(intl)
          : genericError(intl)
      )
      .then((response) =>
        response && response.data
          ? setFaqContent(DOMPurify.sanitize(response.data))
          : null
      );
  }

  function getRentalNames(query, rentalId, checkQuery = true) {
    if (checkQuery && (!query || (query && query.length < 5))) return;
    api(
      {
        method: "post",
        url: `${API_URL}/costs/rentals/names`,
        data: {
          period: period.toString(),
          query,
          rental_id: rentalId,
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.token}`,
        },
      },
      []
    )
      .then((response) => {
        if (response && response.data) {
          setSearchResults(response.data);
        }
      })
      .catch((error) =>
        error.response && error.response.status && error.response.status === 401
          ? logOut(intl)
          : genericError(intl)
      );
  }

  function getRentals(query, rentalId) {
    return api(
      {
        method: "post",
        url: `${API_URL}/costs/rentals`,
        data: {
          period: period.toString(),
          query,
          rental_id: rentalId,
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.token}`,
        },
      },
      []
    )
      .then((response) => response.data)
      .catch((error) =>
        error.response && error.response.status && error.response.status === 401
          ? logOut(intl)
          : genericError(intl)
      );
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedGetRentals = useCallback(
    debounce((query, rentalId) => getRentalNames(query, rentalId), 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedGetRentals.cancel();
    };
  }, [debouncedGetRentals]);

  function searchRentals(query, rentalId = null) {
    Sentry.logger.trace("Searching rentals", { query, rentalId });
    setSearchValue(query);
    if (query === null) {
      getRentalNames(query, rentalId, false);
    } else {
      debouncedGetRentals(query, rentalId);
    }
  }

  function selectRentals(filter, rentalId = null) {
    Sentry.logger.debug("Applying rental filter", { filter, rentalId });

    // Direct load (rentalId provided) or non-admin loading all their rentals
    const isDirectLoad = !!rentalId || !filter;

    // Safety: prevent admins from fetching all rentals (too many)
    if (isDirectLoad && !rentalId && isAdmin) {
      Sentry.logger.warn("Admin selectRentals without rentalId - skipping");
      return;
    }

    // For search: require 5+ chars
    if (!isDirectLoad && filter.length < 5) {
      return;
    }

    getRentals(filter, rentalId).then((response) => {
      Sentry.logger.debug("Rentals loaded", {
        count: response?.length || 0,
        filter,
      });
      setRentalOwnerships(response || []);
      if (filter) {
        setFilter(filter);
      }
      setSearchValue("");
      setSearchResults([]);
    });
  }

  async function getContract(bookingsync_rental_id, landlordId) {
    await api(
      {
        method: "get",
        url: `${API_URL}/costs/rental-ownership?bookingsync_rental_id=${bookingsync_rental_id}&landlord_id=${landlordId}&period=${period.toString()}`,
        headers: { Authorization: `Bearer ${localStorage.token}` },
      },
      []
    ).then((response) => {
      if (response && response.data) {
        const rentalData = response.data.rental;
        setContract({
          // Basic rules
          booking_accounting_method: rentalData.booking_accounting_method,
          owners_take: rentalData.owners_take,
          fixed_profit: rentalData.fixed_profit,
          daily_profit: rentalData.daily_profit,
          minimum_profit: rentalData.minimum_profit,
          commission_toggle: rentalData.commission_toggle,
          commission_ratio: rentalData.commission_ratio,
          settlement_period: rentalData.settlement_period,
          owners_from: rentalData.owners_from,
          // Contract details
          contract_name: rentalData.contract_name,
          contract_description: rentalData.contract_description,
          settleable: rentalData.settleable,
          settlement_type: rentalData.settlement_type,
          // Payment config
          commission_from: rentalData.commission_from,
          default_payment_processing_fee:
            rentalData.default_payment_processing_fee,
          payment_processing_fee_from: rentalData.payment_processing_fee_from,
          cleaning_fee_rule: rentalData.cleaning_fee_rule,
          costs_rule: rentalData.costs_rule,
          // Extras
          owner_extras: rentalData.owner_extras,
          extras_description_full_price:
            rentalData.extras_description_full_price,
          hidden_extras: rentalData.hidden_extras,
          owner_payments: rentalData.owner_payments,
          reimbursements_categories: rentalData.reimbursements_categories,
          visible_fields: rentalData.visible_fields,
          // Rental contract dates
          rental_contract_start: rentalData.rental_contract_start,
          rental_contract_end: rentalData.rental_contract_end,
          rental_contract_visible_fields:
            rentalData.rental_contract_visible_fields,
        });
        setLandlords(rentalData.landlords);
      }
    });
  }

  async function getRentalOwnership(bookingsync_rental_id) {
    await api(
      {
        method: "get",
        url: `${API_URL}/costs/rental-ownership?bookingsync_rental_id=${bookingsync_rental_id}&period=${period.toString()}`,
        headers: { Authorization: `Bearer ${localStorage.token}` },
      },
      []
    )
      .then(
        (response) =>
          response &&
          response.data &&
          setRentalOwnerships(
            rentalOwnerships.map((rentalOwnership) => {
              if (rentalOwnership.id === response.data.id) {
                return response.data;
              }
              return rentalOwnership;
            })
          )
      )
      .catch((error) =>
        error.response && error.response.status && error.response.status === 401
          ? logOut(intl)
          : genericError(intl)
      );
  }

  function handleRejectionSubmit(values) {
    Sentry.logger.debug("Rejection form submitted", {
      settlementId: settlement?.id,
      noteLength: values.note?.length || 0,
    });
    handleSettlementSubmit(false, values.note);
  }

  function handleApproveSubmit(values) {
    Sentry.logger.debug("Approval form submitted", {
      settlementId: settlement?.id,
      selfInvoicing: rental?.self_invoicing,
    });
    handleSettlementSubmit(true, "");
  }

  async function handleSettlementSubmit(approved, note = "") {
    const action = approved ? "approve" : "reject";
    Sentry.logger.info(`Settlement ${action} started`, {
      settlementId: settlement?.id,
      rentalId: rental?.bookingsync_rental_id,
      rentalName: rental?.name,
      period: period.toString(),
      note: note ? "note_provided" : "no_note",
    });

    setIsSettlementLoading(true);

    await api(
      {
        method: "post",
        url: `${API_URL}/costs/accept`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.token}`,
        },
        data: {
          ownership_id: rentalOwnership && rentalOwnership.id,
          settlement_id: settlement && settlement.id,
          period: period.toString(),
          date: dayjs().format(),
          approved,
          note,
        },
      },
      []
    )
      .then((response) => {
        Sentry.logger.debug(`Settlement ${action}ed`, {
          settlementId: settlement?.id,
          rentalId: rental?.bookingsync_rental_id,
          rentalName: rental?.name,
          period: period.toString(),
          wireTransferAmount: response.data.wire_transfer_amount,
        });

        setSettlement(response.data);
        getRentalOwnership(rental && rental.bookingsync_rental_id);

        Sentry.logger.info(`Settlement ${action}ed`, {
          settlementId: response.data.id,
          rentalId: rental?.bookingsync_rental_id,
        });
      })
      .catch((error) => {
        const status = error.response?.status;
        if (status === 401) {
          logOut(intl);
        } else {
          Sentry.logger.error(`Settlement ${action} failed`, {
            settlementId: settlement?.id,
            rentalId: rental?.bookingsync_rental_id,
            period: period.toString(),
          });
          genericError(intl, error, { endpoint: "/costs/accept" });
        }
      });
    reset({ note: "" });
    setIsSettlementLoading(false);
    setRejectNoteVisible(false);
    setInvoiceAcceptNoteVisible(false);
  }

  function renderApproveRejectButtons() {
    const isSettlementAvailable =
      settlement && settlement.approved === null && !viewOnly;
    if (selectedRental && settlement && settlement.rental === selectedRental) {
      if (settlement.approved === true) {
        const text = `${
          settlement.status === "Automatyczna akceptacja"
            ? intl.formatMessage({ id: "settlement-auto-approved" })
            : intl.formatMessage({ id: "settlement-approved-part-1" })
        } ${dayjs(settlement.date).format("DD/MM/YYYY")} - ${intl.formatMessage(
          { id: "settlement-approved-part-2" }
        )} ${settlement.id}`;
        return <h3>{text}</h3>;
      }
      if (settlement.approved === false) {
        return (
          <>
            <h3>
              {`${intl.formatMessage({ id: "settlement-rejected" })} ${dayjs(
                settlement.date
              ).format("DD/MM/YYYY")}`}
            </h3>
            <div className="reject-note-container">
              <p>
                <strong>
                  {intl.formatMessage({ id: "settlement-reject-note" })}
                </strong>
                :{settlement.note}
              </p>
            </div>
            <Button
              type="primary"
              onClick={() => handleSettlementSubmit(true)}
              disabled={isSettlementLoading || viewOnly}
            >
              {intl.formatMessage({ id: "approve-settlement" })}
            </Button>
          </>
        );
      }
    }
    return (
      <>
        <Button
          type="primary"
          onClick={() => {
            Sentry.logger.debug("Approve button clicked", {
              settlementId: settlement?.id,
              selfInvoicing: rental?.self_invoicing,
            });
            rental && rental.self_invoicing
              ? setInvoiceAcceptNoteVisible(true)
              : handleSettlementSubmit(true);
          }}
          disabled={!isSettlementAvailable || isSettlementLoading}
          style={
            !isSettlementAvailable || isSettlementLoading
              ? {}
              : { backgroundColor: "green", color: "white" }
          }
        >
          {intl.formatMessage({ id: "approve-settlement" })}
        </Button>
        <Button
          type="primary"
          onClick={() => {
            Sentry.logger.debug("Reject button clicked", {
              settlementId: settlement?.id,
            });
            setRejectNoteVisible(true);
          }}
          disabled={!isSettlementAvailable || isSettlementLoading}
          style={
            !isSettlementAvailable || isSettlementLoading
              ? {}
              : { backgroundColor: "red", color: "white" }
          }
        >
          {intl.formatMessage({ id: "reject-settlement" })}
        </Button>
      </>
    );
  }

  function exportSettlementToPDF() {
    Sentry.logger.debug("PDF export started", {
      rentalName: rental.name,
      rentalId: rental.bookingsync_rental_id,
      period: period.toString(),
      settlementId: settlement?.id,
    });

    var element = document.getElementById("settlement-summary");
    setCompanyDataVisible(true);
    var opt = {
      margin: [0.5, 0.5],
      filename: `${rental.name}_${period}.pdf`,
      jsPDF: {
        unit: "cm",
        format: "a4",
        orientation: "landscape",
        autopaging: false,
      },
      html2canvas: { scale: 2, letterRendering: true },
      pagebreak: { mode: "avoid-all" },
    };
    html2pdf()
      .from(element)
      .set(opt)
      .save()
      .then(() => {
        Sentry.logger.debug("PDF export completed", {
          filename: `${rental.name}_${period}.pdf`,
          rentalId: rental.bookingsync_rental_id,
        });
        setCompanyDataVisible(false);
      })
      .catch((error) => {
        Sentry.captureException(error, {
          contexts: {
            custom: {
              message: "PDF export failed",
              rentalName: rental.name,
              period: period.toString(),
            },
          },
        });
        Sentry.logger.error("PDF export failed", {
          error: error.message,
          rentalName: rental.name,
          period: period.toString(),
        });
        setCompanyDataVisible(false);
      });
  }

  // Redirect to include landlord ID if missing from URL
  useEffect(() => {
    if (rental && rentalOwnership && !selectedLandlord && period) {
      navigate(
        dashboardPath(
          rentalOwnership.rental.bookingsync_rental_id,
          rentalOwnership.landlord.id,
          period,
          activeTab
        ),
        { replace: true }
      );
    }
  }, [rental, rentalOwnership, selectedLandlord, period, activeTab, navigate]);

  // Normalize the URL once a rental is selected: append the default tab to
  // legacy tab-less links and rewrite unknown or admin-only slugs.
  useEffect(() => {
    if (!pathRentalId || !pathLandlordId || !period) return;

    const normalizedTab = normalizeDashboardTab(pathTab, isAdmin);
    if (pathTab === normalizedTab) return;

    if (pathTab) {
      Sentry.logger.warn("Unknown or forbidden dashboard tab", {
        tab: pathTab,
        isAdmin,
      });
    }

    navigate(
      dashboardPath(pathRentalId, pathLandlordId, period, normalizedTab),
      { replace: true }
    );
  }, [pathRentalId, pathLandlordId, pathTab, period, isAdmin, navigate]);

  // Initial data loading:
  // - Admins with pathRentalId: fetch only that rental (performance optimization)
  // - Admins without pathRentalId: do nothing, wait for search
  // - Owners: always fetch all their rentals (pathRentalId only used for pre-selection)
  if (rentalOwnerships?.length === 0 && !triedLoading) {
    if (isAdmin && pathRentalId) {
      // Admin refresh/direct link - load just this rental
      setTriedLoading(true);
      selectRentals(null, pathRentalId);
    } else if (!isAdmin) {
      // Owner - always load all their rentals
      setTriedLoading(true);
      selectRentals(null);
    }
    // Admin without pathRentalId: do nothing, wait for search
  }

  // Auto-select rental when admin searches and selects from dropdown
  useEffect(() => {
    if (filter) {
      selectRental(filter, null, period);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentalOwnerships, filter]);

  // Auto-select first rental if none selected
  useEffect(() => {
    if (
      rentals.length > 0 &&
      !selectedRental &&
      rentalOwnerships?.length > 0 &&
      !rental &&
      period
    ) {
      performRentalChange(
        rentalOwnerships[0],
        rentalOwnerships[0].landlord.id,
        period,
        true,
        true
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentals.length, selectedRental, rentalOwnerships, rental, period]);

  // Redirect to valid period if current period is before cut-off (non-admin users)
  useEffect(() => {
    if (rental && landlord && period && !isAdmin) {
      const cutOff = CUT_OFF[REACT_APP_BRAND_NAME]?.[settlement_period];
      if (
        cutOff &&
        (Number(period.year) < cutOff["year"] ||
          (Number(period.year) === cutOff["year"] &&
            period.month.id <= cutOff["month"]))
      ) {
        const validPeriod = Period.fromString(cutOff["from"]);
        navigate(
          dashboardPath(
            rental.bookingsync_rental_id,
            landlord.id,
            validPeriod,
            activeTab
          ),
          { replace: true }
        );
      }
    }
  }, [rental, landlord, period, settlement_period, activeTab, navigate]);

  // Redirect to correct period type if rental's settlement_period doesn't match URL period
  useEffect(() => {
    if (rental && landlord && period && rental.settlement_period) {
      if (
        rental.settlement_period === "QUARTERLY" &&
        settlement_period === "MONTHLY"
      ) {
        const quarterlyPeriod = period.toQuarter();
        navigate(
          dashboardPath(
            rental.bookingsync_rental_id,
            landlord.id,
            quarterlyPeriod,
            activeTab
          ),
          { replace: true }
        );
      } else if (
        rental.settlement_period === "MONTHLY" &&
        settlement_period === "QUARTERLY"
      ) {
        const monthlyPeriod = period.toMonth();
        navigate(
          dashboardPath(
            rental.bookingsync_rental_id,
            landlord.id,
            monthlyPeriod,
            activeTab
          ),
          { replace: true }
        );
      }
    }
  }, [rental, landlord, period, settlement_period, activeTab, navigate]);

  const filters = (
    <div
      id="filters"
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        height: 85,
        overflow: "auto",
      }}
    >
      {isAdmin ? (
        <>
          <Select
            value={searchValue !== "" ? searchValue : rental ? rental.name : ""}
            showSearch
            style={{ width: 400 }}
            optionFilterProp="children"
            onChange={(filter) => selectRentals(filter)}
            onSearch={(value) => searchRentals(value)}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            notFoundContent={
              <Empty
                className="empty-sign-small"
                description={
                  (searchValue && searchValue.length) <= 5
                    ? intl.formatMessage({ id: "no-rental-search-query" })
                    : intl.formatMessage({ id: "no-rental" })
                }
              />
            }
          >
            {filteredSearchResults &&
              filteredSearchResults.map((result) => (
                <Option value={result} key={result}>
                  {result}
                </Option>
              ))}
          </Select>
          {selectedRental && rental && landlords && (
            <Select
              value={landlord && landlord.name}
              showSearch
              style={{ width: 400 }}
              optionFilterProp="children"
              onSearch={() => {}}
              onFocus={() => {}}
              onBlur={() => {}}
              onChange={(landlordId) =>
                selectRental(rental.name, landlordId, period)
              }
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              notFoundContent={
                <Empty
                  className="empty-sign-small"
                  description={intl.formatMessage({ id: "no-landlords" })}
                />
              }
            >
              {landlords &&
                landlords.map((landlord) => (
                  <Option value={landlord.id} key={landlord.id}>
                    {landlord.name}
                  </Option>
                ))}
            </Select>
          )}
        </>
      ) : (
        <Select
          value={rental ? rental.name : ""}
          showSearch
          style={{ width: 400 }}
          optionFilterProp="children"
          onSearch={() => {}}
          onFocus={() => {}}
          onBlur={() => {}}
          onChange={(filter) => selectRental(filter, null, period)}
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          notFoundContent={
            <Empty
              className="empty-sign-small"
              description={intl.formatMessage({ id: "no-rental" })}
            />
          }
        >
          {rentals &&
            rentals.map((rentalName) => (
              <Option value={rentalName} key={rentalName}>
                {rentalName}
              </Option>
            ))}
        </Select>
      )}
      {selectedRental && contract && rental && landlord && (
        <PeriodSelect
          period={period}
          onSelect={(period) => selectRental(rental.name, landlord.id, period)}
          onCheck={(period) => checkIfPeriodAvaialble(rentalOwnership, period)}
          settlement_period={settlement_period}
          brand={rental.brand || ""}
        />
      )}
    </div>
  );

  function getCompanyInfo(department) {
    if (COMPANY_INFO[department]) {
      return COMPANY_INFO[department];
    }
    if (COMPANY_INFO[REACT_APP_BRAND_NAME]) {
      return COMPANY_INFO[REACT_APP_BRAND_NAME];
    }
    return null;
  }

  const companyInfo = rental ? getCompanyInfo(rental.department) : null;

  const isFinal =
    contract && ["FINAL_GROSS", "FINAL_NET"].includes(contract.owners_from);

  const items = [
    {
      label: intl.formatMessage({ id: "settlements" }),
      key: DASHBOARD_TABS.SETTLEMENTS,
      children: (
        <>
          <div>
            {selectedRental && rental && contract ? (
              <>
                {contract.booking_accounting_method !== "FLAT_FEE" ? (
                  <div className="table-divider">
                    <h2>
                      {`${intl.formatMessage({ id: "bookings" })} (${
                        bookings.filter((b) => b.days > 0).length
                      })`}
                    </h2>
                    <ReservationsTable
                      bookings={bookings.filter(
                        (b) =>
                          b.comments.includes(
                            "Zaliczka z anulowanej rezerwacji"
                          ) || b.days > 0
                      )}
                      columns={columns}
                      commission_ratio={rental.commission_ratio}
                    />
                  </div>
                ) : (
                  <div className="table-divider">
                    <h2>{intl.formatMessage({ id: "revenues" })}</h2>
                  </div>
                )}
                <div style={{ marginBottom: 20 }}>
                  {settlement || contract ? (
                    <h4>
                      {`${intl.formatMessage({ id: "fixed-profit" })}: ${
                        contract.fixed_profit
                      } PLN`}
                    </h4>
                  ) : null}
                  {!isFinal && settlement && (
                    <>
                      <h4>
                        {`${intl.formatMessage({
                          id: "owner-income-sum",
                        })}: ${settlement.owner_income_sum} PLN`}
                      </h4>
                      {rental.vat_taxpayer && settlement && (
                        <h4>
                          {`${intl.formatMessage({
                            id: "gross-owner-income-sum",
                          })}: ${settlement.gross_owner_income_sum} PLN`}
                        </h4>
                      )}
                    </>
                  )}
                </div>
                <div className="table-divider">
                  <h2>
                    {`${intl.formatMessage({ id: "costs" })} (${costs.length})`}
                  </h2>
                  <CostTable costs={costs} />
                </div>
                {isFinal && settlement ? (
                  <div style={{ marginBottom: 20 }}>
                    <h4>
                      {`${intl.formatMessage({
                        id: "owner-income-sum",
                      })}: ${settlement.owner_income_sum} PLN`}
                    </h4>
                    {rental.vat_taxpayer && (
                      <h4>
                        {`${intl.formatMessage({
                          id: "gross-owner-income-sum",
                        })}: ${settlement.gross_owner_income_sum} PLN`}
                      </h4>
                    )}
                  </div>
                ) : null}
                {isFinal ? (
                  <div className="table-divider">
                    <h2>
                      {`${intl.formatMessage({ id: "reimbursements" })} (${
                        reimbursements.length
                      })`}
                    </h2>
                    <CostTable costs={reimbursements} showSum={false} />
                  </div>
                ) : null}
                <div className="table-divider">
                  <h2>{intl.formatMessage({ id: "settlement" })}</h2>
                </div>
                <div style={{ marginBottom: 20 }}>
                  {!["FINAL_GROSS", "FINAL_NET"].includes(
                    contract.owners_from
                  ) &&
                    settlement && (
                      <h4>
                        {`${intl.formatMessage({ id: "final-sum" })}: ${
                          settlement.final_sum
                        } PLN`}
                      </h4>
                    )}
                  <h4>
                    {`${intl.formatMessage({ id: "current-balance" })}:
                  ${
                    settlement
                      ? settlement.current_balance
                      : rental.current_balance
                  }
                  PLN`}
                  </h4>
                </div>
              </>
            ) : (
              <Empty
                className="empty-sign"
                description={intl.formatMessage({ id: "select-rental" })}
              />
            )}
            <div>
              {settlement && (
                <Col span={12}>
                  <Statistic
                    title={intl.formatMessage({
                      id: "wire-transfer-amount",
                    })}
                    value={`${settlement.wire_transfer_amount} PLN`}
                  />
                </Col>
              )}
              {rental &&
                rental.self_invoicing &&
                settlement &&
                settlement.invoice_view_url &&
                settlement.invoice_view_url !== "" && (
                  <h3>
                    <a
                      href={settlement && settlement.invoice_view_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {intl.formatMessage({ id: "settlement-invoice" })}
                    </a>
                  </h3>
                )}
            </div>
            <div>
              {rental && rental.drafts && (
                <>
                  {settlement && settlement.approved === null && (
                    <div>
                      <h3>
                        {intl.formatMessage({ id: "settlement-awaiting" })}
                      </h3>
                    </div>
                  )}
                  {renderApproveRejectButtons()}
                </>
              )}
            </div>
            {rental && !rental.drafts && !settlement && (
              <div
                style={{
                  paddingRight: 10,
                  paddingLeft: 10,
                  backgroundColor: "#f55c47",
                  fontSize: "1rem",
                  width: 180,
                  height: 30,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {intl.formatMessage({ id: "no-settlement" })}
              </div>
            )}
            <div id="company-data">
              {companyDataVisible && companyInfo && (
                <Card
                  title={intl.formatMessage({ id: "company-info" })}
                  style={{ width: "30%", marginTop: 30 }}
                >
                  <p>{companyInfo.name}</p>
                  <p>{companyInfo.street}</p>
                  <p>{companyInfo.city}</p>
                  <p>{companyInfo.nip}</p>
                </Card>
              )}
            </div>
          </div>
          {selectedRental &&
            settlement &&
            settlement.rental === selectedRental &&
            settlement.approved === true && (
              <div style={{ marginTop: 20 }}>
                <Button onClick={exportSettlementToPDF}>
                  {intl.formatMessage({ id: "download-pdf-summary" })}
                </Button>
              </div>
            )}
        </>
      ),
    },
    {
      label: intl.formatMessage({ id: "calendar" }),
      key: DASHBOARD_TABS.CALENDAR,
      children: (
        <>
          {(isAdmin || rental?.owner_bookings_enabled) &&
            selectedRental &&
            rental && (
              <Button
                type="primary"
                style={{ marginBottom: 16 }}
                onClick={() => setOwnerBookingVisible(true)}
              >
                {intl.formatMessage({ id: "owner-booking-title" })}
              </Button>
            )}
          <Calendar
            bookings={bookings.filter(
              (b) => !b.comments.includes("Zaliczka z anulowanej rezerwacji")
            )}
            period={period}
            selected={selectedRental}
          />
        </>
      ),
    },
    {
      label: intl.formatMessage({ id: "rental-info" }),
      key: DASHBOARD_TABS.RENTAL_INFO,
      children: <RentalInfo rental={rental} />,
    },
    {
      label: intl.formatMessage({ id: "settlement-rules" }),
      key: DASHBOARD_TABS.SETTLEMENT_RULES,
      children: (
        <SettlementRules
          rental={rental}
          contract={contract}
          rentalShare={viewOnly ? 0 : rentalShare}
          ownership={rentalOwnership}
        />
      ),
    },
    isAdmin && {
      label: intl.formatMessage({ id: "landlord-info" }),
      key: DASHBOARD_TABS.LANDLORD_INFO,
      children: <LandlordInfo landlord={landlord} />,
    },
    {
      label: intl.formatMessage({ id: "invoices" }),
      key: DASHBOARD_TABS.INVOICES,
      children: (
        <InvoiceList rental={rental} period={period} landlord={landlord} />
      ),
    },
    {
      label: intl.formatMessage({ id: "faq" }),
      key: DASHBOARD_TABS.FAQ,
      children: faqContent ? (
        <div
          dangerouslySetInnerHTML={{
            __html: faqContent,
          }}
        />
      ) : (
        <div>
          <div>{intl.formatMessage({ id: "no-faq-1" })}</div>
          <div>{intl.formatMessage({ id: "no-faq-2" })}</div>
        </div>
      ),
    },
  ].filter(Boolean);

  return (
    <Layout filters={filters} renderLocaleSwitcher>
      <div
        id="settlement-summary"
        style={{
          display: "flex",
          flexDirection: "column",
          // alignItems: "center",
          width: "100%",
          height: "100%",
          overflow: "auto",
        }}
      >
        {companyDataVisible ? filters : null}
        <div>
          <Tabs
            activeKey={activeTab}
            items={items}
            onChange={(key) => {
              setPendingTab(key);
              Sentry.logger.debug("Tab changed", {
                tab: key,
                rentalId: rental?.bookingsync_rental_id,
              });
              if (selectedRental && selectedLandlord && period) {
                // The tab is view state, not a destination: the URL still
                // carries it so links and reloads work, but replacing keeps
                // Back pointing at whatever came before the dashboard.
                navigate(
                  dashboardPath(selectedRental, selectedLandlord, period, key),
                  { replace: true }
                );
              }
            }}
          />
        </div>
        <Modal
          title={intl.formatMessage({ id: "approve-settlement" })}
          open={invoiceAcceptNoteVisible}
          footer={[
            <Button
              key="back"
              onClick={() => {
                setInvoiceAcceptNoteVisible(false);
              }}
            >
              {intl.formatMessage({ id: "cancel" })}
            </Button>,
            <Button
              key="submit"
              type="primary"
              disabled={isSettlementLoading}
              onClick={handleSubmit(handleApproveSubmit)}
            >
              {intl.formatMessage({ id: "approve-settlement-and-invoice" })}
            </Button>,
          ]}
        >
          {intl.formatMessage({ id: "confirm-approve-settlement-and-invoice" })}
        </Modal>
        <Modal
          title={intl.formatMessage({ id: "reject-settlement" })}
          open={rejectNoteVisible}
          onCancel={() => {
            reset({ note: "" });
            setRejectNoteVisible(false);
          }}
          footer={[
            <Button
              key="back"
              onClick={() => {
                reset({ note: "" });
                setRejectNoteVisible(false);
              }}
            >
              {intl.formatMessage({ id: "cancel" })}
            </Button>,
            <Button
              key="submit"
              type="primary"
              disabled={isSettlementLoading}
              onClick={handleSubmit(handleRejectionSubmit)}
            >
              {intl.formatMessage({ id: "send" })}
            </Button>,
          ]}
        >
          <form onSubmit={() => setRejectNoteVisible(false)}>
            <Controller
              name="note"
              control={control}
              defaultValue=""
              rules={{ required: true }}
              render={({ field }) => (
                <TextArea
                  {...field}
                  rows={4}
                  maxLength={255}
                  placeholder={intl.formatMessage({
                    id: "settlement-reject-note-reason",
                  })}
                />
              )}
            />
            {errors.note &&
              intl.formatMessage({
                id: "settlement-reject-note-reason-required",
              })}
          </form>
        </Modal>
        <OwnerBookingModal
          open={ownerBookingVisible}
          onClose={() => setOwnerBookingVisible(false)}
          onSuccess={() => {
            setOwnerBookingVisible(false);
            if (selectedRental && selectedLandlord && period) {
              getSettlement(selectedRental, selectedLandlord, period);
            }
          }}
          bookings={bookings}
          selectedRental={selectedRental}
          contractEnd={rental?.contract_end}
        />
      </div>
    </Layout>
  );
}
